import React from 'react';
import { useAuth0 } from '../../../auth/useAuth0';
import { useTranslation, Trans } from 'react-i18next';
import { IconLock } from '@tabler/icons-react';
import { useBusinessContext } from '../../../context/BusinessContext';

/**
 * Fallback screen for an authenticated team member who has NO access to any
 * area of the currently-selected business.
 *
 * This is intentionally NOT the Calendar (or any other screen) — a member with
 * zero permissions must never silently land on a functional screen. The redirect
 * logic in HomeComponent can't pick a "first allowed" screen when none exist, so
 * it renders this dead-end instead. The header (business switcher + logout) stays
 * mounted above it, so a user who DOES have access to another business can switch
 * to it, and anyone can log out.
 */
const NoAccessComponent: React.FC = () => {
    const { t } = useTranslation();
    const { logout } = useAuth0();
    const { allBusiness, selectedBusiness } = useBusinessContext();
    const hasOtherBusiness = (allBusiness?.length ?? 0) > 1;

    return (
        <div
            data-cy="noaccess-page"
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.85rem',
                textAlign: 'center',
                padding: '3rem 2rem',
                minHeight: '60vh',
            }}
        >
            <div
                style={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(114, 102, 232, 0.12)',
                    color: '#7266e8',
                }}
            >
                <IconLock size={36} stroke={1.8} />
            </div>

            <h2 style={{ margin: 0, color: '#2a2245' }}>{t('noAccess.title')}</h2>

            <p style={{ maxWidth: 460, color: '#6c757d', margin: 0 }}>
                {/* <Trans> so the <1>…</1> in the message renders the business name in
                    bold instead of showing the literal "<1>" markup (issue #49). */}
                <Trans
                    i18nKey="noAccess.hint"
                    values={{ business: selectedBusiness?.name ?? t('noAccess.thisBusiness') }}
                    components={{ 1: <strong style={{ color: '#2a2245' }} /> }}
                />
            </p>

            {hasOtherBusiness && (
                <p style={{ maxWidth: 460, color: '#6c757d', margin: 0, fontSize: '0.9rem' }}>
                    {t('noAccess.switchHint')}
                </p>
            )}

            <button
                type="button"
                className="btn btn-outline-secondary mt-2"
                onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                data-cy="noaccess-logout-btn"
            >
                {t('header.logout')}
            </button>
        </div>
    );
};

export default NoAccessComponent;
